(() => {
  function quantityPricing(price, quantity) {
    const percent = quantity >= 30 ? 20 : quantity >= 20 ? 15 : quantity >= 10 ? 10 : 0;
    const original = price * quantity;
    const total = Math.round(original * (100 - percent) / 100);
    return { percent, total, saving: original - total, unit: total / quantity };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { quantityPricing };
  if (typeof customElements === 'undefined') return;
  if (customElements.get('pp-ayodhya-box')) return;
  class AyodhyaFamily extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.track = this.querySelector('[data-ab-family-track]');
      if (!this.track) return;
      this.querySelector('[data-ab-family-controls]').hidden = false;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-ab-family-prev]')) this.go(this.index() - 1);
        if (event.target.closest('[data-ab-family-next]')) this.go(this.index() + 1);
      }, options);
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.go(this.index() + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      }, { ...options, passive: true });
      this.sync();
    }
    index() { return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth)); }
    go(index) {
      const count = this.track.children.length;
      const next = (index + count) % count;
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    sync() { this.querySelector('[data-ab-family-counter]').textContent = `${this.index() + 1} / ${this.track.children.length}`; }
    disconnectedCallback() { this.listeners?.abort(); cancelAnimationFrame(this.frame); }
  }
  customElements.define('pp-ayodhya-family', AyodhyaFamily);
  class AyodhyaBox extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.lightObserver?.disconnect();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.setupLights(options);
      this.config = JSON.parse(this.querySelector('[data-ab-config]').textContent);
      this.deliveryConfig = JSON.parse(this.querySelector('[data-ab-delivery-config]').textContent);
      this.comparison = JSON.parse(this.querySelector('[data-ab-comparison-config]').textContent);
      this.form = this.querySelector('form');
      this.variant = this.querySelector('[data-ab-variant]');
      this.quantity = this.querySelector('[data-ab-quantity]');
      this.gallery = this.querySelector('[data-ab-gallery]');
      this.sticky = this.querySelector('[data-ab-sticky]');
      if (!this.form || !this.variant || !this.quantity) return;
      this.formatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: this.config.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.wholeFormatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: this.config.currency, maximumFractionDigits: 0 });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-ab-compare-select]')) this.syncComparison();
        else this.sync();
      }, options);
      this.addEventListener('input', event => { if (event.target === this.quantity) this.sync(); }, options);
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('play', event => {
        if (event.target.tagName !== 'VIDEO') return;
        this.querySelectorAll('video').forEach(video => { if (video !== event.target) video.pause(); });
      }, { ...options, capture: true });
      const reviews = this.querySelector('.ab-brand-reviews');
      if (reviews) {
        const labelProducts = () => reviews.querySelectorAll('.jdgm-carousel-item__product').forEach(item => {
          const image = item.querySelector('img');
          if (!image?.alt || item.querySelector('.ab-reviewed-product')) return;
          const label = document.createElement('span');
          label.className = 'ab-reviewed-product';
          label.textContent = 'Product reviewed: ' + image.alt;
          item.append(label);
        });
        labelProducts();
        this.reviewObserver = new MutationObserver(labelProducts);
        this.reviewObserver.observe(reviews, { childList: true, subtree: true });
      }
      this.form.addEventListener('submit', event => { event.preventDefault(); this.add(event.submitter?.dataset.abAction || 'cart'); }, options);
      this.gallery.addEventListener('scroll', () => {
        cancelAnimationFrame(this.galleryFrame);
        this.galleryFrame = requestAnimationFrame(() => this.syncGallery());
      }, { ...options, passive: true });
      this.gallery.addEventListener('keydown', event => {
        if (event.target !== this.gallery || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.go(this.galleryIndex() + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      const updateSticky = () => {
        cancelAnimationFrame(this.stickyFrame);
        this.stickyFrame = requestAnimationFrame(() => this.updateSticky());
      };
      window.addEventListener('scroll', updateSticky, { ...options, passive: true });
      window.addEventListener('resize', updateSticky, options);
      window.addEventListener('resize', () => this.syncReviewExcerpts(), options);
      window.addEventListener('pageshow', () => { this.syncDelivery(); this.sync(); this.updateSticky(); }, options);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.syncDelivery(); }, options);
      const drawer = document.querySelector('m-cart-drawer');
      this.drawerObserver = new MutationObserver(updateSticky);
      if (drawer) this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      this.sync();
      this.syncComparison();
      this.syncDelivery();
      this.syncGallery();
      this.updateSticky();
      const choices = this.querySelector('.ab-edition-choices');
      if (choices) choices.hidden = false;
      this.dataset.ready = 'true';
      this.syncReviewExcerpts();
      document.fonts?.ready.then(() => { if (this.isConnected) this.syncReviewExcerpts(); });
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.drawerObserver?.disconnect();
      this.reviewObserver?.disconnect();
      this.lightObserver?.disconnect();
      cancelAnimationFrame(this.galleryFrame);
      cancelAnimationFrame(this.stickyFrame);
    }
    setupLights(options) {
      this.lightToggle = this.querySelector('[data-ab-light-toggle]');
      if (!this.lightToggle) return;
      this.lightMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.lightsPaused = this.lightsPaused ?? (!!navigator.connection?.saveData || this.lightMotion.matches);
      this.lightToggle.addEventListener('click', () => {
        this.lightsPaused = !this.lightsPaused;
        this.syncLights();
      }, options);
      this.lightMotion.addEventListener('change', () => {
        if (this.lightMotion.matches) this.lightsPaused = true;
        this.syncLights();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncLights(), options);
      this.lightObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.target.toggleAttribute('data-ab-lights-visible', entry.isIntersecting));
      });
      this.querySelectorAll('[data-ab-light-stage]').forEach(stage => this.lightObserver.observe(stage));
      this.syncLights();
    }
    syncLights() {
      const reduced = this.lightMotion.matches;
      this.toggleAttribute('data-ab-lights-running', !this.lightsPaused && !reduced && !document.hidden);
      this.lightToggle.hidden = reduced;
      const label = this.lightsPaused ? this.lightToggle.dataset.playLabel : this.lightToggle.dataset.pauseLabel;
      this.lightToggle.setAttribute('aria-label', label);
      this.lightToggle.setAttribute('title', label);
      this.lightToggle.querySelector('[data-ab-light-pause]').hidden = this.lightsPaused;
      this.lightToggle.querySelector('[data-ab-light-play]').hidden = !this.lightsPaused;
    }
    money(cents) { return (Number.isInteger(cents / 100) ? this.wholeFormatter : this.formatter).format(cents / 100); }
    syncReviewExcerpts() {
      this.querySelectorAll('[data-ab-review-quote]').forEach(quote => {
        const button = quote.closest('.ab-video-review').querySelector('[data-ab-review-toggle]');
        button.hidden = quote.dataset.expanded !== 'true' && quote.scrollHeight <= quote.clientHeight + 1;
      });
    }
    syncComparison() {
      const select = this.querySelector('[data-ab-compare-select]');
      const format = this.comparison[select.value];
      if (!format) return;
      this.querySelector('[data-ab-compare-name]').textContent = format.name;
      this.querySelectorAll('[data-ab-compare-value]').forEach(cell => {
        cell.textContent = format.values[Number(cell.dataset.abCompareValue)];
      });
      this.querySelector('[data-ab-compare-control]').hidden = false;
    }
    syncDelivery(now = new Date()) {
      // Use the store's calendar day, independent of visitor timezone and DST.
      const parts = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
      const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
      const start = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day) + 3));
      const end = new Date(start);
      end.setUTCDate(end.getUTCDate() + 2);
      const dateFormat = new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', day: 'numeric', month: 'short' });
      const sameMonth = start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
      const range = sameMonth ? `${start.getUTCDate()}–${dateFormat.format(end)}` : `${dateFormat.format(start)}–${dateFormat.format(end)}`;
      const estimate = this.deliveryConfig.estimate.replace('[range]', range);
      this.querySelectorAll('[data-ab-delivery]').forEach(node => { node.textContent = estimate; });
    }
    selection() {
      const variant = this.config.variants.find(item => String(item.id) === this.variant.value);
      const quantity = Number(this.quantity.value);
      return { variant, quantity, ...quantityPricing(variant?.price || 0, quantity) };
    }
    sync() {
      const selected = this.selection();
      const min = selected.variant?.min || 1;
      this.quantity.min = String(min);
      this.quantity.step = String(selected.variant?.step || 1);
      if (selected.variant?.max > 0) this.quantity.max = String(selected.variant.max);
      else this.quantity.removeAttribute('max');
      const valid = this.quantity.checkValidity() && Number.isSafeInteger(selected.quantity);
      const available = !!selected.variant?.available;
      this.querySelectorAll('[data-ab-unit]').forEach(price => { price.textContent = this.money(selected.variant?.price || 0); });
      this.querySelector('[data-ab-compare-price]').textContent = this.money(selected.variant?.price || 0);
      this.querySelector('[data-ab-total]').textContent = valid ? this.money(selected.total) : 'Check quantity';
      this.querySelector('[data-ab-savings]').textContent = valid && selected.percent ? `${selected.percent}% quantity saving · ${this.money(selected.total / selected.quantity)} per box` : '';
      const natural = selected.variant?.title === 'Preserved Nature';
      this.querySelector('[data-ab-edition-note]').textContent = natural
        ? this.config.strings.naturalNote || 'Real moss, preserved for display. Not a growing plant. No watering.'
        : this.config.strings.classicNote || 'Decorative greenery made with synthetic moss. Not a living plant.';
      this.querySelectorAll('[data-ab-material]').forEach(label => {
        label.textContent = natural ? this.config.strings.realMoss : this.config.strings.artificialMoss;
      });
      this.querySelectorAll('[data-ab-choice]').forEach(button => {
        const variant = this.config.variants.find(item => String(item.id) === button.dataset.abChoice);
        button.disabled = this.pending || !variant?.available;
        button.setAttribute('aria-pressed', String(button.dataset.abChoice === this.variant.value));
      });
      this.querySelector('[data-ab-label]').textContent = this.pending && this.intent === 'cart' ? this.config.strings.adding : available ? this.config.strings.add : this.config.strings.soldOut;
      this.querySelector('[data-ab-buy-label]').textContent = this.pending && this.intent === 'checkout' ? this.config.strings.opening : available ? this.deliveryConfig.buy : this.config.strings.soldOut;
      this.querySelector('[data-ab-add]').disabled = this.pending || this.uncertain || !available || !valid;
      this.querySelector('[data-ab-buy]').disabled = this.pending || this.uncertain || !available || !valid;
      this.variant.disabled = this.pending;
      this.quantity.disabled = this.pending;
      this.querySelector('[data-ab-minus]').disabled = this.pending || selected.quantity <= min;
      this.querySelector('[data-ab-plus]').disabled = this.pending || (!!this.quantity.max && selected.quantity >= Number(this.quantity.max));
      this.querySelectorAll('[data-ab-pack]').forEach(button => {
        const count = Number(button.dataset.abPack);
        const pricing = quantityPricing(selected.variant?.price || 0, count);
        button.querySelector('[data-ab-set-unit]').textContent = this.money(pricing.unit);
        button.querySelector('[data-ab-set-total]').textContent = this.money(pricing.total);
        button.querySelector('[data-ab-set-saving]').textContent = pricing.percent ? `${this.config.strings.setSave} ${this.money(pricing.saving)}` : this.config.strings.setStandard;
        button.disabled = this.pending || !available || count < min || (count - min) % (selected.variant?.step || 1) !== 0 || (!!this.quantity.max && count > Number(this.quantity.max));
        const threshold = selected.quantity >= 30 ? 30 : selected.quantity >= 20 ? 20 : selected.quantity >= 10 ? 10 : 1;
        button.setAttribute('aria-pressed', String(Number(button.dataset.abPack) === threshold));
      });
      this.querySelector('[data-ab-sticky-edition]').textContent = selected.variant?.title || '';
      this.querySelector('[data-ab-sticky-summary]').textContent = valid ? `${selected.quantity} ${selected.quantity === 1 ? 'box' : 'boxes'}` : 'Choose your quantity';
      const stickyPrice = this.querySelector('[data-ab-sticky-price]');
      stickyPrice.textContent = valid ? this.money(selected.total) : '';
      stickyPrice.hidden = this.pending || !available || !valid;
      this.querySelector('[data-ab-sticky-label]').textContent = this.pending ? this.intent === 'checkout' ? this.config.strings.opening : this.config.strings.adding : !available ? this.config.strings.soldOut : this.config.strings.add;
      this.querySelector('[data-ab-sticky-add]').disabled = this.pending || this.uncertain || !available || !valid;
      const enquiry = `Hello Perfect Plants, I'd like help with a bulk Ayodhya Ashirwad Diwali Box order. Edition: ${selected.variant?.title || 'Please advise'}. Quantity: ${valid ? selected.quantity : 'Please advise'}.`;
      this.querySelectorAll('[data-ab-bulk]').forEach(link => { link.href = `https://wa.me/${this.config.whatsapp}?text=${encodeURIComponent(enquiry)}`; });
    }
    galleryIndex() { return Math.round(this.gallery.scrollLeft / Math.max(1, this.gallery.clientWidth)); }
    go(index) {
      const count = this.gallery.children.length;
      const next = (index + count) % count;
      this.gallery.scrollTo({ left: this.gallery.clientWidth * next, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    syncGallery() {
      const index = this.galleryIndex();
      this.querySelectorAll('[data-ab-go]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.abGo) === index)));
      // Leave dimension and front/side reference images free of decoration.
      this.gallery.closest('.ab-gallery-frame').toggleAttribute('data-ab-decorative-hidden', index >= 3);
    }
    onClick(event) {
      const reviewToggle = event.target.closest('[data-ab-review-toggle]');
      if (reviewToggle) {
        const quote = reviewToggle.closest('.ab-video-review').querySelector('[data-ab-review-quote]');
        const expanded = reviewToggle.getAttribute('aria-expanded') !== 'true';
        quote.dataset.expanded = String(expanded);
        reviewToggle.setAttribute('aria-expanded', String(expanded));
        reviewToggle.textContent = expanded ? reviewToggle.dataset.lessLabel : reviewToggle.dataset.moreLabel;
        this.syncReviewExcerpts();
      }
      const pack = event.target.closest('[data-ab-pack]');
      if (pack && !this.pending) { this.quantity.value = pack.dataset.abPack; this.sync(); }
      if (event.target.closest('[data-ab-minus]')) { this.quantity.stepDown(); this.sync(); }
      if (event.target.closest('[data-ab-plus]')) { this.quantity.stepUp(); this.sync(); }
      const thumb = event.target.closest('[data-ab-go]');
      if (thumb) this.go(Number(thumb.dataset.abGo));
      if (event.target.closest('[data-ab-prev]')) this.go(this.galleryIndex() - 1);
      if (event.target.closest('[data-ab-next]')) this.go(this.galleryIndex() + 1);
      const edition = event.target.closest('[data-ab-edition]');
      if (edition && !this.pending) {
        const variant = this.config.variants.find(item => item.title === edition.dataset.abEdition);
        if (variant) { this.variant.value = String(variant.id); this.sync(); }
      }
      const choice = event.target.closest('[data-ab-choice]');
      if (choice && !choice.disabled && !this.pending) {
        this.variant.value = choice.dataset.abChoice;
        this.sync();
      }
      if (event.target.closest('[data-ab-sticky-choose]')) {
        this.querySelector('[data-ab-purchase]').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        (this.querySelector('[data-ab-choice][aria-pressed="true"]') || this.variant).focus({ preventScroll: true });
      }
      const stickyAdd = event.target.closest('[data-ab-sticky-add]');
      if (stickyAdd && !stickyAdd.disabled && !this.pending && !this.uncertain) this.form.requestSubmit(this.querySelector('[data-ab-add]'));
      const play = event.target.closest('[data-ab-video-play]');
      if (play) {
        const media = play.closest('.ab-video-media');
        if (media.querySelector('video')) return;
        this.querySelectorAll('video').forEach(video => video.pause());
        const fragment = media.querySelector('template').content.cloneNode(true);
        media.append(fragment);
        play.hidden = true;
        const video = media.querySelector('video');
        video.play().catch(() => {});
        video.focus();
      }
    }
    updateSticky() {
      if (!this.sticky) return;
      const passed = this.querySelector('[data-ab-purchase]').getBoundingClientRect().bottom < 0;
      const final = this.querySelector('.ab-final').getBoundingClientRect();
      const finalVisible = final.top < innerHeight && final.bottom > 0;
      const drawerOpen = document.querySelector('m-cart-drawer')?.classList.contains('m-cart-drawer--active');
      this.sticky.hidden = !passed || finalVisible || drawerOpen;
    }
    message(text, error = false) {
      const output = this.querySelector('[data-ab-status]');
      output.textContent = text;
      output.hidden = !text;
      output.toggleAttribute('data-error', error);
      output.setAttribute('role', error ? 'alert' : 'status');
    }
    async add(intent = 'cart') {
      if (this.pending || this.uncertain) return;
      const selected = this.selection();
      if (!this.quantity.reportValidity() || !Number.isSafeInteger(selected.quantity) || !selected.variant?.available) return;
      const root = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('m-cart-drawer');
      if (cart?.ppCartBusy?.() || cart?.hasAttribute('data-pp-add-pending') || cart?.hasAttribute('data-pp-add-uncertain')) return;
      const ids = [...new Set((cart?.getSectionsToRender?.() || []).map(section => section.id))].slice(0, 5);
      this.pending = true;
      this.intent = intent;
      this.message('');
      this.sync();
      const trigger = this.querySelector(intent === 'checkout' ? '[data-ab-buy]' : '[data-ab-add]');
      cart?.setActiveElement?.(trigger);
      cart?.setAttribute('data-pp-add-pending', '');
      cart?.ppSyncCart?.();
      if (intent === 'cart') cart?.open?.(trigger);
      let confirmed = false;
      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: selected.variant.id, quantity: selected.quantity }], sections: ids, sections_url: location.pathname + location.search }),
        });
        const result = await response.json();
        if (!response.ok || result.status) { this.message(result.description || this.config.strings.error, true); return; }
        confirmed = true;
        this.message(this.config.strings.added);
        const first = result.items?.[0];
        const cartResult = { ...result, id: first?.id, variant_id: first?.variant_id, product_id: first?.product_id };
        // Preserve the theme's cart integration without sending additional pixel events.
        try {
          const update = window.MinimogTheme?.pubSubEvents?.cartUpdate;
          if (update) window.MinimogEvents?.emit(update, { ...cartResult, source: 'main-cart-items' });
        } catch (_) {}
        if (!cart?.renderContents || !ids.every(id => typeof result.sections?.[id] === 'string')) {
          location.assign(root + 'cart');
          return;
        }
        cart.renderContents(cartResult);
        cart.removeAttribute('data-pp-add-pending');
        cart.ppSyncCart?.();
        if (intent === 'checkout') {
          // Allow Minimog's scheduled drawer opening before using its existing Fastrr control.
          await new Promise(resolve => setTimeout(resolve, 0));
          const host = cart.querySelector('.shiprocket-headless');
          const checkout = host?.querySelector('.sr-headless-checkout');
          if (checkout && !checkout.disabled && checkout.getAttribute('aria-disabled') !== 'true' && getComputedStyle(host).display !== 'none' && !cart.classList.contains('pp-cart-terms-required')) checkout.click();
          else this.message(this.config.strings.checkoutFallback);
        }
      } catch (_) {
        this.uncertain = !confirmed;
        this.message(confirmed ? this.config.strings.checkoutFallback : this.config.strings.uncertain, !confirmed);
        if (cart?.ppConfirmCart) await cart.ppConfirmCart();
        else cart?.onCartDrawerUpdate?.();
        if (confirmed) cart?.open?.(trigger);
      } finally { cart?.removeAttribute('data-pp-add-pending'); cart?.ppSyncCart?.(); this.pending = false; this.sync(); }
    }
  }
  customElements.define('pp-ayodhya-box', AyodhyaBox);

  class AyodhyaMoments extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.config = JSON.parse(this.querySelector('[data-ab-moment-config]').textContent);
      this.interval = Math.min(10000, Math.max(500, Number(this.config.interval) || 500));
      this.request = (this.request || 0) + 1;
      this.querySelectorAll('[data-ab-moment]').forEach((frame, index) => {
        if (index) frame.remove();
        else frame.hidden = false;
      });
      this.templates = [...this.querySelectorAll('[data-ab-moment-template]')];
      this.frames = new Map([[0, this.querySelector('[data-ab-moment]')]]);
      this.index = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || !!navigator.connection?.saveData;
      this.visible = false;
      this.querySelector('[data-ab-moment-buttons]').hidden = false;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-ab-moment-toggle]')) {
          this.paused = !this.paused;
          this.schedule();
        }
        const previous = event.target.closest('[data-ab-moment-prev]');
        const next = event.target.closest('[data-ab-moment-next]');
        if (previous || next) {
          this.paused = true;
          this.schedule();
          this.show(this.index + (previous ? -1 : 1), true);
        }
      }, options);
      this.addEventListener('focusin', event => {
        if (event.target.closest('[data-ab-moment-toggle]')) return;
        this.paused = true;
        this.schedule();
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.schedule();
      }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.25;
          this.schedule();
        }, { threshold: 0.25 });
        this.observer.observe(this);
      }
      this.schedule();
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.request = (this.request || 0) + 1;
    }
    schedule() {
      clearTimeout(this.timer);
      const running = this.visible && !this.paused && !document.hidden;
      this.toggleAttribute('data-running', running);
      const toggle = this.querySelector('[data-ab-moment-toggle]');
      toggle.setAttribute('aria-label', this.paused ? this.config.play : this.config.pause);
      toggle.title = this.paused ? this.config.play : this.config.pause;
      this.querySelector('[data-ab-pause-icon]').hidden = this.paused;
      this.querySelector('[data-ab-play-icon]').hidden = !this.paused;
      if (running) this.timer = setTimeout(() => this.show(this.index + 1), this.interval);
    }
    async show(index, manual = false) {
      const count = this.templates.length + 1;
      const next = (index + count) % count;
      const request = this.request = (this.request || 0) + 1;
      let frame = this.frames.get(next);
      if (!frame) {
        const template = this.templates[next - 1];
        frame = document.createElement('figure');
        frame.hidden = true;
        frame.dataset.abMoment = '';
        frame.dataset.caption = template.dataset.caption;
        frame.append(template.content.cloneNode(true));
        const image = frame.querySelector('img');
        image.loading = 'eager';
        this.querySelector('.ab-moment-photos').append(frame);
        this.frames.set(next, frame);
        try { await image.decode(); } catch (_) {
          frame.remove();
          this.frames.delete(next);
          this.paused = true;
          this.schedule();
          return;
        }
      }
      if (request !== this.request || !this.isConnected) return;
      if (!manual && (this.paused || !this.visible || document.hidden)) return;
      this.frames.forEach((item, number) => { item.hidden = number !== next; });
      this.index = next;
      this.querySelector('[data-ab-moment-caption]').textContent = frame.dataset.caption;
      const counter = this.querySelector('[data-ab-moment-counter]');
      counter.setAttribute('aria-live', manual ? 'polite' : 'off');
      counter.textContent = `${next + 1} / ${count}`;
      this.schedule();
    }
  }
  customElements.define('pp-ayodhya-moments', AyodhyaMoments);
})();
